(() => {
  const roots = document.querySelectorAll('[data-founder-ledger]');
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const milestones = [1, 25, 50, 75, 100];
  const numberWords = [
    'Zero',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
    'Ten',
    'Eleven',
    'Twelve',
    'Thirteen',
    'Fourteen',
    'Fifteen',
    'Sixteen',
    'Seventeen',
    'Eighteen',
    'Nineteen',
    'Twenty'
  ];
  const tensWords = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function toNumber(value, fallback = 0) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function padFounderNumber(value) {
    return String(Math.max(0, Math.round(toNumber(value)))).padStart(3, '0');
  }

  function wordForNumber(value) {
    const number = Math.round(toNumber(value));

    if (numberWords[number]) return numberWords[number];
    if (number < 100) {
      const tens = Math.floor(number / 10);
      const ones = number % 10;
      return ones ? `${tensWords[tens]}-${numberWords[ones].toLowerCase()}` : tensWords[tens];
    }
    if (number === 100) return 'One hundred';

    return String(number);
  }

  function parseState(root) {
    try {
      return JSON.parse(root.dataset.ledgerState || '{}');
    } catch {
      return null;
    }
  }

  function dateLabel(value) {
    if (!value) return '—';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';

    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC'
    }).format(date);
  }

  function daysUntil(value) {
    if (!value) return Infinity;

    const end = new Date(value);
    if (Number.isNaN(end.getTime())) return Infinity;

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    const startOfEnd = new Date(end);
    startOfEnd.setHours(0, 0, 0, 0);

    return Math.ceil((startOfEnd.getTime() - startOfToday.getTime()) / 86400000);
  }

  function isClosed(state) {
    const closesAt = new Date(state.closes_at);
    return Number.isFinite(closesAt.getTime()) && Date.now() > closesAt.getTime();
  }

  function normalizeState(state) {
    const joined = clamp(toNumber(state.joined), 0, 100);
    const nextNumber = toNumber(state.next_number, joined + 1);
    const remaining = toNumber(state.remaining, 100 - joined);
    const rate7d = toNumber(state.rate_7d);
    const hoursSinceOpen = toNumber(state.hours_since_open, Infinity);

    return {
      ...state,
      joined,
      nextNumber,
      remaining,
      rate7d,
      hoursSinceOpen
    };
  }

  function updateDates(root, state) {
    const dateSpans = root.querySelectorAll('[data-ledger-dates] span');
    const milestoneDates = state.milestone_dates || {};

    milestones.forEach((milestone, index) => {
      const element = dateSpans[index];
      if (!element) return;

      if (milestone === 1) {
        element.textContent = dateLabel(state.opened_at);
        return;
      }

      element.textContent = state.joined >= milestone ? dateLabel(milestoneDates[String(milestone)]) : '—';
    });
  }

  function updateSpeed(root, state) {
    const speed = root.querySelector('[data-ledger-speed]');
    if (!speed) return;

    if (state.joined >= 100) {
      speed.hidden = false;
      speed.textContent = 'One hundred places taken.';
      return;
    }

    if (state.joined < 5 || state.rate7d < 3) {
      speed.hidden = true;
      speed.textContent = '';
      return;
    }

    speed.hidden = false;

    if (state.hoursSinceOpen < 72) {
      speed.textContent = root.dataset.firstDaysCopy || `${wordForNumber(state.joined)} founders in the first two days.`;
      return;
    }

    speed.textContent = `${wordForNumber(state.rate7d)} founders joined in the past seven days.`;
  }

  function updateDeadline(root, state) {
    const deadline = root.querySelector('[data-ledger-deadline]');
    if (!deadline) return;

    const daysLeft = daysUntil(state.closes_at);
    const shouldShowCapacityClose = state.remaining <= 15;
    const shouldShowDateClose = daysLeft <= 3;

    if (state.joined >= 100 || !shouldShowCapacityClose && !shouldShowDateClose) {
      deadline.hidden = true;
      deadline.textContent = '';
      return;
    }

    deadline.hidden = false;

    if (daysLeft <= 0) {
      deadline.textContent = 'Last day.';
    } else if (daysLeft === 1) {
      deadline.textContent = 'Last day.';
    } else if (daysLeft === 2) {
      deadline.textContent = 'Two days left.';
    } else if (daysLeft === 3) {
      deadline.textContent = 'Three days left.';
    } else {
      deadline.textContent = root.dataset.closeDateText || 'Closes at one hundred places or 19 August, whichever comes first.';
    }
  }

  function updateCta(root, state) {
    const cta = root.querySelector('[data-ledger-cta]');
    if (!cta) return;

    if (state.joined >= 100) {
      cta.textContent = root.dataset.completeText || 'Founder 100 is complete.';
      cta.removeAttribute('href');
      cta.setAttribute('aria-disabled', 'true');
      return;
    }

    if (isClosed(state)) {
      cta.textContent = `${root.dataset.closedText || 'Founder 100 closed on'} ${dateLabel(state.closes_at)}.`;
      cta.removeAttribute('href');
      cta.setAttribute('aria-disabled', 'true');
      return;
    }

    cta.textContent = `${root.dataset.ctaPrefix || 'Become Founder'} #${padFounderNumber(state.nextNumber)}`;
    cta.removeAttribute('aria-disabled');
  }

  function setProgress(root, joined) {
    const value = clamp(joined, 0, 100);
    const percent = `${value}%`;
    const count = root.querySelector('[data-ledger-count]');
    const current = root.querySelector('[data-ledger-current]');
    const rule = root.querySelector('.fw-ledger__rule');
    const ticks = root.querySelectorAll('[data-ledger-tick]');

    root.style.setProperty('--joined', value);
    root.style.setProperty('--joined-percent', percent);
    if (rule) {
      rule.style.setProperty('--joined', value);
      rule.style.setProperty('--joined-percent', percent);
    }
    if (count) count.textContent = padFounderNumber(value);
    if (current) {
      current.textContent = padFounderNumber(value);
      current.style.left = percent;
    }
    ticks.forEach((tick, index) => {
      tick.classList.toggle('is-active', index < Math.round(value));
    });
  }

  function render(root, state, options = {}) {
    const normalized = normalizeState(state);

    if (options.animate) {
      animateProgress(root, normalized.joined);
    } else {
      setProgress(root, normalized.joined);
    }

    updateDates(root, normalized);
    updateCta(root, normalized);
    updateSpeed(root, normalized);
    updateDeadline(root, normalized);
  }

  function easeOut(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function animateProgress(root, target) {
    const duration = 600;
    const start = performance.now();

    function tick(now) {
      const elapsed = clamp((now - start) / duration, 0, 1);
      setProgress(root, target * easeOut(elapsed));

      if (elapsed < 1) {
        requestAnimationFrame(tick);
      } else {
        setProgress(root, target);
      }
    }

    requestAnimationFrame(tick);
  }

  function reveal(root, state) {
    root.classList.add('is-ledger-ready');

    if (prefersReducedMotion) {
      render(root, state);
      root.classList.add('is-ledger-detail-ready');
      return;
    }

    render(root, state, { animate: true });
    window.setTimeout(() => {
      root.classList.add('is-ledger-detail-ready');
    }, 600);
  }

  async function refreshOnce(root) {
    const endpoint = root.dataset.fetchUrl;
    if (!endpoint) return;

    try {
      const response = await fetch(endpoint, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });

      if (!response.ok) return;

      const freshState = await response.json();
      render(root, freshState);
    } catch {
      // A stale SSR value is better than surfacing a transient network failure.
    }
  }

  roots.forEach((root) => {
    const state = parseState(root);

    if (!state) {
      root.hidden = true;
      return;
    }

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      reveal(root, state);
      refreshOnce(root);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;

      observer.disconnect();
      reveal(root, state);
      refreshOnce(root);
    }, { threshold: 0.3 });

    observer.observe(root);
  });
})();
